import Icon from "@ui/atoms/Icon/Icon";
import styled from "@emotion/styled";
import { Box, Stack } from "@mui/material";
import { BaseButton } from "@ui/atoms/BaseButton/BaseButton";
import BaseTypography from "@ui/atoms/BaseTypography/BaseTypography";
import { Bounce, toast, ToastOptions } from "react-toastify";
import { typography } from "@themes/tokens/shared";

export enum ToastType {
  Error = "error",
  Success = "success",
  Info = "info",
  Warning = "warning"
}

type InfoToastData = {
  title: string;
  content?: string;
  actionTitle?: string;
  action?: () => void;
};

type InfoToastContentProps = {
  closeToast?: () => void;
  data: InfoToastData;
};

type ErrorToastData = {
  message: string;
};

type ErrorToastContentProps = {
  data: ErrorToastData;
};

interface IconRippleProps {
  color: string;
  size: number;
}

export const IconRipple = styled(Box)<IconRippleProps>(({ color, size }) => ({
  alignItems: "center",
  border: `2px solid ${color}`,
  borderRadius: "50%",
  display: "flex",
  height: size,
  justifyContent: "center",
  width: size
}));

function getInfoToastContent({ closeToast, data }: InfoToastContentProps) {
  return (
    <Stack sx={{ width: "100%" }}>
      <IconRipple color="var(--mui-palette-primary-100)" size={38}>
        <IconRipple color="var(--mui-palette-primary-300)" size={28}>
          <Box
            sx={{ color: "var(--mui-palette-primary-main)", display: "flex" }}
          >
            <Icon name="info" />
          </Box>
        </IconRipple>
      </IconRipple>

      <BaseTypography
        variant="body2"
        weight="semiBold"
        sx={[
          { mb: data?.content ? 0 : 1, mt: 1 },
          { color: (theme) => theme.vars.palette.text.tertiary }
        ]}
        text={data.title}
      />

      {data?.content ? (
        <BaseTypography
          variant="body2"
          weight="regular"
          text={data.content}
          sx={{ color: "color.secondary" }}
        />
      ) : null}

      {data?.action ? (
        <Stack sx={{ alignItems: "center", flexDirection: "row" }}>
          <BaseButton
            variant="text"
            text="Dismiss"
            onClick={closeToast}
            color="secondary"
            sx={(theme) => ({
              fontWeight: theme.typography.fontWeightSemiBold,
              pl: 0
            })}
          />
          <BaseButton
            variant="text"
            text={data?.actionTitle || "View Report"}
            onClick={data.action}
            sx={(theme) => ({
              fontWeight: theme.typography.fontWeightSemiBold,
              pl: 0
            })}
          />
        </Stack>
      ) : null}
    </Stack>
  );
}

function getErrorToastContent({ data }: ErrorToastContentProps) {
  return (
    <Stack sx={{ width: "100%" }}>
      <IconRipple color="rgba(255, 255, 255, 0.4)" size={38}>
        <IconRipple color="rgba(255, 255, 255, 0.7)" size={28}>
          <IconRipple color="rgba(255, 255, 255, 0.8)" size={18}>
            <BaseTypography
              variant="caption"
              sx={{
                color: (theme) => theme.vars.palette.common.white,
                fontWeight: typography.fontWeightBold
              }}
              text="!"
            />
          </IconRipple>
        </IconRipple>
      </IconRipple>

      <BaseTypography
        variant="body2"
        weight="regular"
        text={data.message}
        sx={{ color: (theme) => theme.vars.palette.common.white, my: 1 }}
      />
    </Stack>
  );
}

export const showToast = (
  title: string,
  type: ToastType = ToastType.Info,
  content?: string,
  action?: () => void,
  actionTitle?: string,
  uniqueId?: string,
  autoClose: number | false = 5000
) => {
  const isInfoToast = type === ToastType.Info;
  const toastId = uniqueId
    ? `notification-${title}-${uniqueId}`
    : `notification-${title}-${Date.now()}`;

  const toastContent = isInfoToast
    ? ({ closeToast }: { closeToast?: () => void }) =>
        getInfoToastContent({
          closeToast,
          data: { action, actionTitle, content, title }
        })
    : () => getErrorToastContent({ data: { message: title } });

  const toastOptions: ToastOptions<unknown> = {
    autoClose,
    className: `custom-notification ${isInfoToast ? "" : "colored-notification"}`,
    closeOnClick: true,
    containerId: "bottom-right",
    hideProgressBar: false,
    icon: false,
    pauseOnHover: true,
    position: "bottom-right",
    theme: isInfoToast ? undefined : ("colored" as const),
    toastId,
    transition: Bounce,
    type
  };

  toast(toastContent, toastOptions);
};
